/**
 * Swiper dependencies
 *
 * @see https://swiperjs.com/get-started
 */
import { Swiper } from 'swiper';
import { Autoplay, Keyboard, Navigation, Pagination, Scrollbar, Controller } from 'swiper/modules';

/**
 * Initialize the slider.
 *
 * @param {Element} container HTMLElement.
 * @param {Object}  options   Slider parameters.
 *
 * @return {Object} Returns initialized slider instance.
 *
 * @see https://swiperjs.com/swiper-api#parameters
 */
export function SwiperInit( container, options = {} ) {
	// For masonry, nav buttons live outside .swiper in the parent layout container.
	const navContainer = options.variant === 'masonry'
		? ( container.closest( '.slider-masonry__layout' ) || container )
		: container;

	const parameters = {
		autoplay: options?.autoplay ?? false,
		autoHeight: options?.autoHeight ?? false,
		loop: options?.loop ?? true,
		centeredSlides: options?.centeredSlides ?? false,
		createElements: true,
		grabCursor: true,
		initialSlide: 0,
		keyboard: true,
		modules: [Autoplay, Keyboard, Navigation, Pagination, Scrollbar, Controller],
		navigation: options?.navigation
		  ? {
			  prevEl: navContainer.querySelector('.jormi-swiper-prev'),
			  nextEl: navContainer.querySelector('.jormi-swiper-next'),
			}
		  : false,
		pagination: options?.pagination ?? false,
		scrollbar: options?.scrollbar ?? false,
		simulateTouch: options?.simulateTouch ?? true,
		slidesPerView: options.variant === 'single' ? 1 : 'auto',
		spaceBetween: options.variant === 'minimal' ? 24 : options.variant === 'masonry' ? 16 : 16,
		slidesPerGroup: 1,
		on: {},
	};

	if ( options.variant === 'masonry' ) {
		applyMasonryWidthHandlers( parameters );
	}

	const swiper = new Swiper( container, parameters );

	if ( container.classList.contains( 'swiper-controller' ) ) {
		const controlledEl = document.querySelector( '.swiper-controlled' );
		if ( controlledEl ) {
			const controlledSwiper = new Swiper( controlledEl, {
				allowTouchMove: false,
				centeredSlides: false,
				loop: true,
			} );
			swiper.controller.control = controlledSwiper;
		}
	}

	return swiper;
}

/**
 * Masonry width handlers.
 *
 * The active slide is 812px wide; every other slide is 464px. Widths and
 * vertical offsets are CSS-driven (see `.swiper-slide` and
 * `.swiper-slide-active` in style.scss)
 * 
 * How jitter is avoided:
 * 1. Every card has the same rendered height. Cards with no featured image
 * use `.card__image-link--placeholder` so a no-image card doesn't change
 * the height of the wrapper
 *
 * 2. Card body/footer use `visibility: hidden` + `opacity: 0` on inactive
 * slides so the height stays consistent
 * 
 * 3. Swiper's horizontal translate is stabilized by this file (below), so
 * the strip doesn't jump around
 *
 * Swiper reads slide widths to figure out how far to shift on slide change.
 * If it measures the DOM while widths are still animating, it
 * gets in-between values and the left edge of the active slide ens up
 * getting clipped by `.slider-masonry__slides { overflow: hidden }`.
 *
 * Workaround: Tell Swiper that every slide is 464px wide.
 * We still set the real widths (812/464) as inline styles so the CSS
 * transition has concrete values to animate to — Swiper just ignores
 * them for its own math.
 *
 * Timing: Swiper computes the translate the instant the user clicks,
 * *before* any event we can hook into. So the "everything is 464" workaround
 * we tell Swiper has to be in place *between* transition — applied at `init`,
 * and reapplied on `slidesUpdated` (which fires after Swiper's internal
 * rebuilds on resize, loop-fix, etc.).
 */
function applyMasonryWidthHandlers(parameters) {
	const DESKTOP_BP = 992;
	const ACTIVE_PX = 812;
	const INACTIVE_PX = 464;
	const isDesktop = () => window.innerWidth >= DESKTOP_BP;

	// Overwrite Swiper's internal size grid so every slide looks 464px wide
	// to its translate math.
	const patchGrid = (swiper) => {
		if (!isDesktop() || ! swiper.slides || swiper.slides.length === 0) return;
		const gap = swiper.params.spaceBetween || 0;
		const stride = INACTIVE_PX + gap;
		swiper.snapGrid = swiper.slides.map(( _, i ) => i * stride);
		swiper.slidesGrid = [...swiper.snapGrid];
		swiper.slidesSizesGrid = swiper.slides.map(() => INACTIVE_PX);
	};

	// Set the real inline widths (812 for active, 464 for everything else).
	// The CSS `transition: width` on `.swiper-slide` animates the change.
	const applyWidths = (swiper) => {
		if (!isDesktop() || !swiper.slides) return;
		const activeIdx = swiper.activeIndex;
		swiper.slides.forEach((slide, i) => {
			slide.style.width = (i === activeIdx ? ACTIVE_PX : INACTIVE_PX) + 'px';
		});
	};

	parameters.on.init = (swiper) => {
		applyWidths(swiper);
		patchGrid(swiper);

		// Reapply on resize
		let resizeTimer;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				swiper.update();
				applyWidths(swiper);
				patchGrid(swiper);
			}, 150);
		});
	};

	// Swiper rebuilds its size grid on init, resize, and loop-fix.
	// Reapply the override
	parameters.on.slidesUpdated = (swiper) => {
		patchGrid(swiper);
	};

	// Refresh inline widths when the active slide changes 
	parameters.on.slideChange = (swiper) => {
		applyWidths(swiper);
	};
}
